import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Chip, CircularProgress, Container, Divider, Paper, Typography } from '@mui/material';
import { EventBusy, MarkEmailRead, Schedule } from '@mui/icons-material';
import Wordmark from '../../components/common/Wordmark';
import vendorOnboardingService, { ResolvedInvite } from '../../services/vendorOnboardingService';

/**
 * Public vendor invite landing page.
 *
 * Reached at /vendor-onboarding/invite?token=... — the exact URL shape
 * produced by vendorOnboardingService.invite()'s buildInviteUrl() on the
 * backend. Deliberately imports nothing that assumes a signed-in staff
 * user: no AuthContext, no tenant context, no protected apiClient
 * assumptions beyond the one public GET the backend actually exposes
 * (GET /vendor-onboarding/invite/:token).
 *
 * The backend's resolveInvite() returns a deliberately minimal, non-
 * sensitive projection of the applicant's own onboarding row — it does
 * NOT include the name of the company that sent the invite (no tenant
 * join in that query), so this page does not claim to show one. Nor does
 * the API expose any public endpoint for the applicant to submit KYC data
 * or documents themselves (every write route requires a signed-in staff
 * session) — this page is therefore an honest read-only confirmation of
 * the invite, not a submission form that would silently go nowhere.
 */
const VendorInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<ResolvedInvite | null>(null);

  useEffect(() => {
    if (!token) {
      setError('This invitation link is missing its access token.');
      setLoading(false);
      return;
    }
    vendorOnboardingService
      .resolveInvite(token)
      .then((data) => setInvite(data))
      .catch((err: any) =>
        setError(err.message || 'This invite link is invalid or has expired.')
      )
      .finally(() => setLoading(false));
  }, [token]);

  const daysRemaining = invite
    ? Math.max(0, Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 6,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Wordmark size={40} tone="light" />
          </Box>

          <Paper
            elevation={0}
            sx={{ p: { xs: 3, sm: 4 }, width: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Checking your invitation...
                </Typography>
              </Box>
            ) : error ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <EventBusy sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  This link isn't valid anymore
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {error}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invite links are single-use and time-limited. Please ask the company you're onboarding with
                  to send a new one.
                </Typography>
              </Box>
            ) : invite ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <MarkEmailRead color="success" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Vendor onboarding invitation
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  This invitation is confirmed and active.
                </Typography>

                <Divider sx={{ mb: 2.5 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Applicant
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {invite.legalName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Reference
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {invite.referenceNo}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Email on file
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {invite.email}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Link expires
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {new Date(invite.expiresAt).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    {daysRemaining !== null && (
                      <Chip
                        size="small"
                        sx={{ mt: 0.5 }}
                        color={daysRemaining <= 2 ? 'warning' : 'default'}
                        label={daysRemaining === 0 ? 'Expires today' : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`}
                      />
                    )}
                  </Box>
                </Box>

                <Alert severity="info">
                  Your onboarding contact will use the details already on file to complete KYC verification and
                  document review. If any of your information needs to change, please reply to your invitation
                  email or contact them directly — this page does not currently accept document uploads.
                </Alert>
              </Box>
            ) : null}
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
            Powered by Stratify Vendor Onboarding
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default VendorInvitePage;
